import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { LiliTextInput } from '@lili/lili-frontend-common';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { t as tGlobal } from 'i18next';
import eyeIcon from '@Assets/icons/eye.svg';
import eyeVisibleIcon from '@Assets/icons/eye-visible.svg';
import { OnboardingFormFields } from '@Types/types.ts';
import { useAvoidAutoComplete } from '@Hooks/custom/useAvoidAutoComplete.ts';

export const passportScheme = z.object({
  [OnboardingFormFields.PASSPORT_NUMBER]: z
    .string()
    .trim()
    .min(1, tGlobal('enErrors:required'))
    .min(7, tGlobal('enErrors:passport.invalid'))
    .max(10, tGlobal('enErrors:passport.invalid'))
    .regex(/^[A-Za-z0-9]+$/, {
      message: tGlobal('enErrors:passport.invalid'),
    }),
});

export const getConditionalPassportScheme = (isRequired: boolean) => {
  if (isRequired) {
    return passportScheme;
  }
  return z.object({
    [OnboardingFormFields.PASSPORT_NUMBER]: z.string().optional(),
  });
};

interface PassportInputProps {
  tabIndex?: number;
}

export const PassportInput = ({ tabIndex }: PassportInputProps = {}) => {
  const { t } = useTranslation('enJSON');
  const [isPassportVisible, setIsPassportVisible] = useState(true);
  useAvoidAutoComplete({ idsToAvoidAutoComplete: [OnboardingFormFields.PASSPORT_NUMBER] });

  const passportHideSlot = () => {
    return (
      <button
        onClick={() => setIsPassportVisible((prevState) => !prevState)}
        className='z-50 absolute top-1/2 -translate-y-1/2 right-[12px] h-[24px] w-[24px]'>
        <img
          alt='hide'
          className='w-full object-cover'
          src={isPassportVisible ? eyeIcon : eyeVisibleIcon}
        />
      </button>
    );
  };

  return (
    <Controller
      name={OnboardingFormFields.PASSPORT_NUMBER}
      render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
        <LiliTextInput
          filterType='alphanum'
          tabIndex={tabIndex}
          maxLength={9}
          value={value}
          title={t('pages.nonUsIdentity.passport')}
          onChange={onChange}
          onBlur={onBlur}
          showClear={false}
          id={OnboardingFormFields.PASSPORT_NUMBER}
          accessoryItemComponent={passportHideSlot()}
          type={isPassportVisible ? 'text' : 'password'}
          errorMessage={error?.message}
          isPublic={false}
          data-testid={OnboardingFormFields.PASSPORT_NUMBER}
        />
      )}
    />
  );
};
